"use client";

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { SparklesIcon, TerminalIcon, TrendingUpIcon, type LucideIcon } from "lucide-react";
import Image from "next/image";

/**
 * Real customer quotes, used with permission — the customers sent these for the
 * landing (Nino and Erik by email, Patrick via his public LinkedIn post); the
 * source + permission trail lives in the team's sales dataroom (not committed
 * here). Lightly trimmed for length, attributed by name + role.
 *
 * Shared between the hire-agent landing (control) and the film landing
 * (treatment, which carousels verbatim excerpts) so the A/B arms show the
 * same people and stay in sync when edited.
 */
export interface Testimonial {
  quote: MessageDescriptor;
  name: string;
  role: string;
  icon: LucideIcon;
  photo?: string;
}

export const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      msg`Most AI tools hand you a chat box. This one hands you a colleague who works while I sleep. I wake up to screened dealflow, not an empty prompt.`,
    name: "Nino Subotic",
    role: "Karaoke Club",
    icon: TrendingUpIcon,
    photo: "/testimonials/nino-subotic.jpg",
  },
  {
    quote:
      msg`I'm an EHS guy, not a developer. I describe the system in my head out loud, and by evening I'm clicking through a working tool. Fifteen years of know-how turned into software.`,
    name: "Erik Mattsson",
    role: "EHS consultant",
    icon: TerminalIcon,
    photo: "/testimonials/erik-mattsson.jpg",
  },
  {
    quote:
      msg`My new best friend George spent the past few days building out an online research library — almost 300 talks, 3M+ words, queryable with sources. I'm exceedingly fond of his proactive mindset: he proposes future steps and shows clear motion toward our shared goal.`,
    name: "Patrick Couch",
    role: "AI Business Developer & Public Speaker",
    icon: SparklesIcon,
    photo: "/testimonials/patrick-couch.jpg",
  },
];

export function TestimonialCard({
  quote,
  name,
  role,
  icon: Icon,
  photo,
}: {
  quote: MessageDescriptor;
  name: string;
  role: string;
  icon: LucideIcon;
  photo?: string;
}) {
  const { i18n } = useLingui();
  return (
    <figure className="flex flex-col border border-border-subtle bg-surface-elevated p-7">
      <blockquote className="flex-1 text-base leading-7 text-foreground">“{i18n._(quote)}”</blockquote>
      <figcaption className="mt-6 flex items-center gap-3">
        {photo ? (
          <Image
            src={photo}
            alt={name}
            width={40}
            height={40}
            className="size-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-full bg-signal/15 text-signal">
            <Icon className="size-5" />
          </span>
        )}
        <span className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">{name}</span>
          <span className="text-xs text-foreground-muted">{role}</span>
        </span>
      </figcaption>
    </figure>
  );
}
